/**
 * RepConnect - Supabase Client Configuration
 *
 * Main Supabase client setup and authentication utilities
 * Replaces localStorage-based authentication system
 */

import { createClient } from '@supabase/supabase-js'
import offlineSyncManager from './offlineSync'
import { logger } from './logger';

// Supabase configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

// Detect if running in Capacitor (iOS/Android native app)
const isCapacitor = typeof window !== 'undefined' && window.Capacitor !== undefined;

logger.debug('🔧 Supabase config:', {
  url: supabaseUrl,
  hasKey: !!supabaseAnonKey,
  isCapacitor,
  envKeys: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_SUPABASE'))
})

// Validate environment variables
let supabaseConfigured = !!(supabaseUrl && supabaseAnonKey);
if (!supabaseUrl || !supabaseAnonKey) {
  logger.warn('Missing Supabase environment variables! Falling back to localStorage auth', {
    url: supabaseUrl,
    hasKey: !!supabaseAnonKey
  })
  supabaseConfigured = false;
} else {
  logger.debug('✅ Supabase configured successfully - will use database storage with localStorage fallback');
}

// Create Supabase client with session persistence
// For Capacitor apps, we need simpler auth configuration
console.log('[Supabase] Creating client, isCapacitor:', isCapacitor);

export const supabase = supabaseConfigured ? createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false, // Disable for both - we handle auth manually
    storage: window.localStorage,
    storageKey: 'repconnect-auth-token',
    flowType: 'pkce', // Use PKCE for both web and iOS
  },
}) : null;

// Fallback localStorage authentication for when Supabase is unavailable
const localAuthFallback = {
  signUp: async (email, password, userData) => {
    logger.debug('📱 Using localStorage fallback for signup');
    const userId = `user_${Date.now()}`;
    const user = {
      id: userId,
      email,
      created_at: new Date().toISOString()
    };
    const profile = {
      id: userId,
      user_type: userData.userType || 'sales',
      first_name: userData.firstName || '',
      last_name: userData.lastName || '',
      email: email,
      onboarding_completed: false
    };
    
    // Store in localStorage
    localStorage.setItem(`auth_user_${email}`, JSON.stringify({ password, user, profile }));
    localStorage.setItem('repconnect_current_user', JSON.stringify({ user, profile }));
    
    // Queue for sync when online
    offlineSyncManager.addToSyncQueue({
      type: 'user_registration',
      data: { email, password, userData }
    });
    
    return { user, profile };
  },
  
  signIn: async (email, password) => {
    logger.debug('📱 Using localStorage fallback for signin');
    
    // Check for demo account first
    if (email === 'rep@repconnect.com' && password === 'REPthisguy11!?') {
      // Use a fixed UUID for the demo user to ensure consistency
      const demoUserId = 'b61e2c70-651c-4f9e-8ebe-1795d5ce65b7'; // Fixed UUID for demo

      const demoUser = {
        id: demoUserId,
        email: 'rep@repconnect.com',
        created_at: new Date().toISOString()
      };
      const demoProfile = {
        id: demoUserId,
        user_type: 'sales',
        userType: 'sales', // Add both formats for compatibility
        first_name: 'Demo',
        last_name: 'Rep',
        email: 'rep@repconnect.com',
        onboarding_completed: true
      };
      localStorage.setItem('repconnect_current_user', JSON.stringify({ user: demoUser, profile: demoProfile }));
      return { user: demoUser, profile: demoProfile };
    }
    
    // Check stored accounts
    const stored = localStorage.getItem(`auth_user_${email}`);
    if (stored) {
      const { password: storedPassword, user, profile } = JSON.parse(stored);
      if (password === storedPassword) {
        localStorage.setItem('repconnect_current_user', JSON.stringify({ user, profile }));
        return { user, profile };
      }
    }
    throw new Error('Invalid login credentials');
  },
  
  getCurrentUser: async () => {
    const stored = localStorage.getItem('repconnect_current_user');
    if (stored) {
      const { user, profile } = JSON.parse(stored);
      return { user, profile };
    }
    return { user: null, profile: null };
  },
  
  signOut: async () => {
    localStorage.removeItem('repconnect_current_user');
  }
};

/**
 * Authentication Service
 * Uses Supabase with localStorage fallback
 */
export const authService = {
  /**
   * Sign up a new user with profile data
   */
  signUp: async (email, password, userData) => {
    // Try Supabase first, fallback to localStorage if it fails
    if (!supabaseConfigured || !supabase) {
      return await localAuthFallback.signUp(email, password, userData);
    }
    
    try {
      // Create auth user
      logger.debug('🔄 Creating Supabase auth user for:', email);
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: userData.firstName,
            last_name: userData.lastName,
            user_type: userData.userType || 'customer'
          }
        }
      })

      logger.debug('🔍 Auth signup result:', {
        user: authData?.user?.id,
        email: authData?.user?.email,
        error: authError
      });
      
      if (authError) {
        logger.error('Supabase auth error', { error: authError.message });
        throw authError;
      }
      
      // If user was created successfully, create user profile
      if (authData.user) {
        try {
          logger.debug('🔄 Creating user profile for:', authData.user.id);
          const profileData = {
            id: authData.user.id,
            user_type: userData.userType || 'customer',
            first_name: userData.firstName || '',
            last_name: userData.lastName || '',
            email: email,
            phone: userData.phone || null,
            company: userData.company || null,
            territory: userData.territory || null,
            onboarding_completed: false
          };

          logger.debug('📄 Profile data to insert:', profileData);

          const { data: profile, error: profileError } = await supabase
            .from('user_profiles')
            .insert([profileData])
            .select()
            .single()

          logger.debug('🔍 Profile creation result:', {
            profile: profile?.id,
            error: profileError
          });
          
          if (profileError) {
            logger.error('Profile creation error', { error: profileError.message })
            // Return basic profile structure so onboarding can proceed
            return { 
              user: authData.user, 
              profile: {
                id: authData.user.id,
                user_type: userData.userType || 'customer',
                first_name: userData.firstName || '',
                last_name: userData.lastName || '',
                email: email,
                onboarding_completed: false
              }
            }
          }
          
          return { user: authData.user, profile }
        } catch (profileCreationError) {
          logger.error('Profile creation failed', { error: profileCreationError.message })
          // Return basic profile so app can continue
          return { 
            user: authData.user, 
            profile: {
              id: authData.user.id,
              user_type: userData.userType || 'customer',
              first_name: userData.firstName || '',
              last_name: userData.lastName || '',
              email: email,
              onboarding_completed: false
            }
          }
        }
      }
      
      return authData
    } catch (error) {
      logger.error('Supabase sign up error', { error: error.message });

      // Don't fall back to localStorage for authentication errors that should be shown to user
      if (error.message && (
        error.message.includes('User already registered') ||
        error.message.includes('already exists') ||
        error.message.includes('Invalid email') ||
        error.message.includes('Password should be at least') ||
        error.message.includes('Email rate limit exceeded')
      )) {
        throw error; // Re-throw authentication errors to show to user
      }

      // Only fall back to localStorage for connectivity/configuration issues
      logger.debug('Falling back to localStorage due to connectivity issue');
      return await localAuthFallback.signUp(email, password, userData);
    }
  },

  /**
   * Sign in with email (magic link for production)
   */
  signIn: async (email, password = null) => {
    // Try Supabase first, fallback to localStorage if it fails
    if (!supabaseConfigured || !supabase) {
      return await localAuthFallback.signIn(email, password);
    }

    try {
      // For production, use magic link authentication
      if (!password) {
        // Use production base URL (remove /api if present for auth redirects)
        const apiUrl = process.env.REACT_APP_API_URL || 'https://login.meetwithreps.com';
        const baseUrl = apiUrl.replace(/\/api$/, '');

        const { data, error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: `${baseUrl}/auth/callback`
          }
        })

        if (error) throw error

        return {
          user: null,
          profile: null,
          message: 'Magic link sent! Check your email to sign in.'
        }
      }

      // Fallback to password authentication for development/demo
      const signInPromise = supabase.auth.signInWithPassword({
        email,
        password,
      })

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Sign in timeout')), 8000)
      )

      const { data, error } = await Promise.race([signInPromise, timeoutPromise])

      if (error) throw error

      // Load user profile with timeout already built in
      if (data.user) {
        const profile = await authService.getUserProfile(data.user.id)
        return { ...data, profile }
      }

      return data
    } catch (error) {
      console.log('[Supabase] Sign in error:', error.message, error);
      logger.error('Supabase sign in error, falling back to localStorage', { error: error.message })
      // Don't fall back for invalid credentials - that's a real error from Supabase
      if (error.message?.includes('Invalid login credentials')) {
        throw error; // Re-throw so the UI shows the error
      }
      return await localAuthFallback.signIn(email, password);
    }
  },

  /**
   * Send magic link for passwordless authentication
   */
  sendMagicLink: async (email) => {
    if (!supabaseConfigured || !supabase) {
      throw new Error('Supabase not configured for magic link authentication');
    }

    try {
      // Use production base URL (remove /api if present for auth redirects)
      const apiUrl = process.env.REACT_APP_API_URL || 'https://login.meetwithreps.com';
      const baseUrl = apiUrl.replace(/\/api$/, '');

      const { data, error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${baseUrl}/auth/callback`
        }
      })

      if (error) throw error

      return {
        success: true,
        message: 'Magic link sent! Check your email to sign in.'
      }
    } catch (error) {
      logger.error('Magic link error', { error: error.message })
      throw error
    }
  },

  /**
   * Send password reset email
   */
  resetPassword: async (email) => {
    if (!supabaseConfigured || !supabase) {
      throw new Error('Supabase not configured for password reset');
    }

    try {
      // Use production base URL (remove /api if present for auth redirects)
      const apiUrl = process.env.REACT_APP_API_URL || 'https://login.meetwithreps.com';
      const baseUrl = apiUrl.replace(/\/api$/, '');

      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${baseUrl}/auth/reset-password`
      })

      if (error) throw error

      return {
        success: true,
        message: 'Password reset email sent! Check your email for the reset link.'
      }
    } catch (error) {
      logger.error('Password reset error', { error: error.message })
      throw error
    }
  },

  /**
   * Update password (after clicking reset link)
   */
  updatePassword: async (newPassword) => {
    if (!supabaseConfigured || !supabase) {
      throw new Error('Supabase not configured');
    }

    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (error) throw error

      return {
        success: true,
        message: 'Password updated successfully!'
      }
    } catch (error) {
      logger.error('Password update error', { error: error.message })
      throw error
    }
  },

  /**
   * Sign out current user
   */
  signOut: async () => {
    if (!supabaseConfigured || !supabase) {
      return await localAuthFallback.signOut();
    }
    
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      await localAuthFallback.signOut(); // Also clear localStorage
    } catch (error) {
      logger.error('Sign out error', { error: error.message })
      await localAuthFallback.signOut(); // Fallback to localStorage clear
    }
  },

  /**
   * Get current user
   */
  getCurrentUser: async () => {
    if (!supabaseConfigured || !supabase) {
      return await localAuthFallback.getCurrentUser();
    }
    
    try {
      const { data: { user }, error } = await supabase.auth.getUser()
      if (error) throw error
      
      if (user) {
        const profile = await authService.getUserProfile(user.id)
        return { user, profile }
      }
      
      return { user: null, profile: null }
    } catch (error) {
      logger.error('Get current user error, trying localStorage fallback', { error: error.message })
      return await localAuthFallback.getCurrentUser();
    }
  },

  /**
   * Get effective user for office staff (returns physician if delegating, self otherwise)
   * Use this in views to ensure office staff accesses physician's data
   */
  getEffectiveUser: async () => {
    const { user, profile } = await authService.getCurrentUser();

    if (!user || !profile) {
      return { user: null, profile: null };
    }

    // If office staff, return the physician they're managing
    if (profile.user_type === 'office_staff' && profile.delegatingFor) {
      logger.debug('👔 Office staff - returning physician as effective user:', profile.delegatingFor.name);
      // Create a user object that represents the physician but maintains staff context
      return {
        user: { ...user, id: profile.delegatingFor.id },
        profile: profile.delegatingFor,
        isStaffMember: true,
        staffProfile: profile, // Keep reference to actual logged-in staff
        staffId: profile.id
      };
    }

    // For all other users, return themselves
    return { user, profile };
  },

  /**
   * Get user profile by ID
   */
  getUserProfile: async (userId) => {
    try {
      logger.debug('Fetching user profile for userId:', userId);

      // Add timeout to prevent hanging
      const profilePromise = supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single()

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Profile fetch timeout')), 5000)
      )

      const { data, error } = await Promise.race([profilePromise, timeoutPromise])

      if (error) {
        logger.error('Profile fetch error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
          status: error.status
        });

        if (error.code === 'PGRST116') {
          // No profile found - this is okay for new users
          logger.debug('No profile found for user, returning null');
          return null
        }

        // Handle 406 Not Acceptable error
        if (error.status === 406 || error.code === '406') {
          logger.warn('406 error on profile fetch - possible RLS or format issue, returning null');
          return null
        }

        throw error
      }

      logger.debug('Profile fetched successfully:', data);

      // If user is office staff, fetch the physician they manage
      if (data && data.user_type === 'office_staff') {
        logger.debug('Office staff detected, fetching managed physician...');

        try {
          const { data: adminRelationship, error: adminError } = await supabase
            .from('admin_users')
            .select('customer_id, role, status')
            .eq('staff_id', userId)
            .in('status', ['active', 'accepted'])
            .single();

          if (adminError) {
            logger.error('Failed to fetch admin relationship:', {
              error: adminError.message,
              code: adminError.code,
              staffId: userId
            });
          } else if (adminRelationship) {
            logger.debug('Found admin relationship:', {
              customerId: adminRelationship.customer_id,
              role: adminRelationship.role,
              status: adminRelationship.status,
              staffId: userId
            });

            // Fetch the full physician profile
            const { data: physicianProfile, error: physicianError } = await supabase
              .from('user_profiles')
              .select('*')
              .eq('id', adminRelationship.customer_id)
              .single();

            if (physicianError) {
              logger.error('Failed to fetch physician profile:', physicianError);
              // Fallback to just ID
              data.delegating_for = adminRelationship.customer_id;
              data.delegatingFor = { id: adminRelationship.customer_id };
            } else {
              logger.debug('Fetched physician profile:', physicianProfile);
              // Set both snake_case and camelCase versions
              data.delegating_for = adminRelationship.customer_id;
              data.delegatingFor = {
                id: physicianProfile.id,
                name: `${physicianProfile.first_name} ${physicianProfile.last_name}`.trim(),
                fullName: `${physicianProfile.first_name} ${physicianProfile.last_name}`.trim(),
                email: physicianProfile.email,
                phone: physicianProfile.phone,
                company: physicianProfile.company,
                location: physicianProfile.location,
                specialty: physicianProfile.specialty,
                npi_number: physicianProfile.npi_number,
                user_type: physicianProfile.user_type,
                ...physicianProfile
              };
              data.adminRole = adminRelationship.role;
              logger.debug('Office staff managing physician:', data.delegatingFor.name);
            }
          } else {
            logger.warn('Office staff has no admin relationship in database');
          }
        } catch (adminFetchError) {
          logger.error('Error fetching admin relationship:', adminFetchError);
        }
      }

      return data
    } catch (error) {
      logger.error('Get user profile error', {
        error: error.message,
        code: error.code,
        status: error.status
      })
      return null
    }
  },

  /**
   * Update user profile
   */
  updateUserProfile: async (userId, updates) => {
    // Use localStorage fallback when Supabase is not available
    if (!supabaseConfigured || !supabase) {
      logger.debug('📱 Using localStorage fallback for profile update');
      
      // Update localStorage
      const currentUserData = localStorage.getItem('repconnect_current_user');
      if (currentUserData) {
        const { user, profile } = JSON.parse(currentUserData);
        const updatedProfile = {
          ...profile,
          ...updates,
          updated_at: new Date().toISOString()
        };
        
        localStorage.setItem('repconnect_current_user', JSON.stringify({ user, profile: updatedProfile }));
        
        // Queue for sync when online
        offlineSyncManager.addToSyncQueue({
          type: 'profile_update',
          data: { userId, updates }
        });
        
        return updatedProfile;
      }
      
      throw new Error('No user profile found in localStorage');
    }
    
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', userId)
        .select()
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      logger.error('Update user profile error', { error: error.message })
      throw error
    }
  },

  /**
   * Complete onboarding for a user
   */
  completeOnboarding: async (userId, onboardingData) => {
    // Use localStorage fallback when Supabase is not available
    if (!supabaseConfigured || !supabase) {
      logger.debug('📱 Using localStorage fallback for onboarding completion');
      
      // Update the stored user profile in localStorage
      const currentUserData = localStorage.getItem('repconnect_current_user');
      if (currentUserData) {
        const { user, profile } = JSON.parse(currentUserData);
        const updatedProfile = {
          ...profile,
          ...onboardingData,
          onboarding_completed: true,
          updated_at: new Date().toISOString()
        };
        
        localStorage.setItem('repconnect_current_user', JSON.stringify({ user, profile: updatedProfile }));
        
        // Queue for sync when online
        offlineSyncManager.addToSyncQueue({
          type: 'onboarding_completion',
          data: { userId, onboardingData }
        });
        
        return updatedProfile;
      }
      
      // If no current user data, create a basic profile
      const basicProfile = {
        id: userId,
        ...onboardingData,
        onboarding_completed: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      // Queue for sync when online
      offlineSyncManager.addToSyncQueue({
        type: 'onboarding_completion',
        data: { userId, onboardingData }
      });
      
      return basicProfile;
    }
    
    try {
      logger.debug('🔍 Attempting to complete onboarding for user:', userId)
      logger.debug('📄 Onboarding data:', onboardingData)
      
      // First, try to get the existing profile (don't throw on not found)
      const { data: existingProfile, error: selectError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle() // Use maybeSingle instead of single to avoid error on no rows
      
      if (selectError) {
        logger.error('Error checking for existing profile', { error: selectError.message })
      }
      
      logger.debug('🔍 Existing profile check result:', existingProfile ? 'Found' : 'Not found')
      
      const updates = {
        ...onboardingData,
        onboarding_completed: true,
        updated_at: new Date().toISOString()
      }
      
      if (existingProfile) {
        // Profile exists, update it
        logger.debug('📝 Updating existing profile...')
        const { data, error } = await supabase
          .from('user_profiles')
          .update(updates)
          .eq('id', userId)
          .select()
          .single()
        
        if (error) {
          logger.error('Update profile error', { error: error.message })
          throw error
        }
        logger.debug('✅ Profile updated successfully:', data)
        return data
      } else {
        // Profile doesn't exist, create it
        logger.debug('➕ Creating new profile during onboarding for user:', userId)
        const newProfile = {
          id: userId,
          email: onboardingData.email || '', // Add email if available
          ...updates
        }
        
        logger.debug('📄 New profile data:', newProfile)
        
        const { data, error } = await supabase
          .from('user_profiles')
          .insert([newProfile])
          .select()
          .single()
        
        if (error) {
          logger.error('Insert profile error', { error: error.message })
          throw error
        }
        logger.debug('✅ Profile created successfully:', data)
        return data
      }
    } catch (error) {
      logger.error('Complete onboarding error', {
        error: error.message,
        code: error.code,
        details: error.details
      })
      throw error
    }
  }
}

/**
 * Auth state change listener
 * Use this in your main App component
 */
export const useAuthStateChange = (callback) => {
  return supabase.auth.onAuthStateChange(callback)
}

/**
 * Check if Supabase is properly configured
 */
export const isSupabaseConfigured = () => {
  return !!(supabaseUrl && supabaseAnonKey && 
           supabaseUrl !== 'your-project-url-here' && 
           supabaseAnonKey !== 'your-anon-key-here')
}

// Export offline sync manager for use in components
export { default as offlineSyncManager } from './offlineSync'

export default supabase